import React, {useState, useEffect} from 'react';
import ClientIntakeForm from './ClientIntakeForm';
import ClientInsuranceForm from './ClientInsuranceForm';
import {ClientThunks} from './clientThunks';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from '../../store/store';
import {Client} from '../../types/client';

interface ClientsFormProps {
  closeModal: () => void;
}

const ClientForm: React.FC<ClientsFormProps> = ({closeModal}) => {
  const [activeTab, setActiveTab] = useState<'general' | 'insurance'>(
    'general',
  );

  const client = useSelector((state: RootState) => state.client.client);
  const initialClientData = client ?? {
    firstName: '',
    lastName: '',
    locationId: '',
    sex: '',
  };
  const [clientData, setClientData] = useState<any>(initialClientData);
  const [isSaving, setIsSaving] = useState(false);
  const [pictureDeleted, setPictureDeleted] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  // Update clientData when client changes (for editing mode)
  useEffect(() => {
    if (client) {
      setClientData(client);
    }
  }, [client]);

  const isIntakeValid = Boolean(
    clientData.firstName &&
      clientData.lastName &&
      clientData.locationId &&
      clientData.sex,
  );

  const isInsuranceValid = Boolean(
    !clientData.clientInsurances || 
    clientData.clientInsurances.length === 0 ||
    clientData.clientInsurances.every((insurance: any) => 
      insurance.payerId && 
      insurance.asOfDate && 
      insurance.untilDate
    )
  );

  const handleNext = () => {
    setActiveTab('insurance');
  };

  const handleBack = () => {
    setActiveTab('general');
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const clientInstance = new Client({
        ...clientData,
        removePicture: pictureDeleted
      });
      if (client?.id) {
        await dispatch(ClientThunks.update(clientInstance));
      } else {
        await dispatch(ClientThunks.create(clientInstance));
      }
      closeModal();
    } catch (error) {
      console.error('Error saving client:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full">
      <div className="flex border-b border-gray-200 dark:border-neutral-700">
        <button
          onClick={() => setActiveTab('general')}
          className={` py-2 text-sm font-medium ${
            activeTab === 'general'
              ? 'border-b-2 border-cyan-400 text-cyan-400'
              : 'text-slate-500 hover:text-slate-700'
          }`}>
          General Info
        </button>
        <button
          onClick={() => isIntakeValid && setActiveTab('insurance')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'insurance'
              ? 'border-b-2 border-cyan-400 text-cyan-400'
              : 'text-slate-500 hover:text-slate-700'
          } ${!isIntakeValid ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={!isIntakeValid}>
          Insurance Info
        </button>
      </div>
      <div>
        {activeTab === 'general' && (
          <ClientIntakeForm
            closeModal={closeModal}
            clientData={clientData}
            setClientData={setClientData}
            onNext={handleNext}
            onSave={handleSave}
            isIntakeValid={isIntakeValid}
            pictureDeleted={pictureDeleted}
            setPictureDeleted={setPictureDeleted}
          />
        )}
        {activeTab === 'insurance' && (
          <ClientInsuranceForm
            closeModal={closeModal}
            clientData={clientData}
            setClientData={setClientData}
            onBack={handleBack}
            onSave={handleSave}
            isSaving={isSaving}
            isInsuranceValid={isInsuranceValid}
          />
        )}
      </div>
    </div>
  );
};
export default ClientForm;
